import {RESULT_PAGE_SIZE} from './constants.js';
import {Store} from './utils/store.js';
import {
	fetchPriceRangeFromState,
	fetchResultsFromState,
	getCachedPredictedResultOrFetch,
	getSimilarResults,
	fetchFacetsFromState,
	fetchProductStickers,
} from './fetchresults.js';
import {SearchResultState} from './searchresultstate.js';
import {createSuggestedQueries} from './utils/createsuggestedqueries.js';
import * as recentSearches from './recentsearches.js';
import { setState as setHistoryState } from './utils/history.js';
import {calcFilteredAttributes} from './utils/calcfilteredattributes.js'
import { trackSearch } from './tracking.js';

export function createStore(state){
	var store;

	function calcWrap(start, mod, limit){
		if (start === null) start = -1;
		var index = start + 1;
		var max = limit + 1;
		var mod = (index + mod + max) % max;
		var value = Math.abs(mod) - 1;
		if (value === -1 || isNaN(value)) value = null;
		return value;
	}

	var fns = {
		'INIT': function(action, state){
			state.recentQueries.items = recentSearches.getAll();

			if (!state.isSearchPage) return state;

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'SEARCH_RESULT_FETCHED',
					query: query,
					results: res
				});
			});

			fetchPriceRangeFromState(state, ({min, max, query}) => {
				store.dispatch({
					type: 'PRICE_RANGE_FETCHED',
					query,
					min,
					max
				});
			});

			return state;
		},

		'INPUT_FORM_SUBMIT': function(action, state){
			recentSearches.addValue(action.query);
			return state;
		},

		'SEARCH': function(action, state){
			var updatePriceRange = state.search.query != state.activeInputQuery

			state.search.state = SearchResultState.LOADING;
			state.search.query = state.activeInputQuery;
			state.search.similarQueries = [];
			state.search.items = [];
			state.search.similarItems = [];
			state.search.page = 0;
			state.search.totalPages = 0;
			state.search.totalItems = 0;
			state.search.price.min = null;
			state.search.price.max = null;

			state.suggestions.queries = [];
			state.suggestions.activeQuery = null;
			state.suggestions.items = [];

			recentSearches.addValue(state.search.query);
			state.recentQueries.items = recentSearches.getAll();

			trackSearch(state.search.query);

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'SEARCH_RESULT_FETCHED',
					query: query,
					results: res
				});
			});

			if (updatePriceRange){
				fetchPriceRangeFromState(state, ({min, max, query}) => {
					store.dispatch({
						type: 'PRICE_RANGE_FETCHED',
						query,
						min,
						max
					});
				});
			}

			return state;
		},

		SEARCH_RESULT_FETCHED: function(action, state){
			if (action.query !== state.search.query) return state;

			state.search.state = SearchResultState.LOADED;
			state.search.items = action.results.hits.hits.map(function(o){
				return o._source;
			});
			
			if (state.search.page === 0) state.search.page = 1;

			state.search.totalItems = action.results.hits.total.value;
			state.search.totalPages = Math.min(10000, Math.ceil(action.results.hits.total.value / RESULT_PAGE_SIZE));

			if (!action.historyRestore){
				setHistoryState({
					searchQuery: state.search.query,
					searchCategories: state.search.categories,
					searchAttributes: state.search.attributes,
					searchMinPrice: state.search.price.min,
					searchMaxPrice: state.search.price.max,
				});
			}
			if (state.showAttributeFilter && state.isSearchPage){
				fetchFacetsFromState(state, ({res:results, query, attributes}) => {
					store.dispatch({
						type: 'FACETS_FETCHED',
						query,
						attributes,
						results,
					});
				});
			}

			if (state.search.items.length === 0 && state.isSearchPage){
				getSimilarResults(store, state);
			}

			fetchProductStickers(state, ({config, products}) => {
				store.dispatch({
					type: 'PRODUCT_STICKERS_FETCHED',
					config,
					products,
				});
			});

			return state;
		},

		PRODUCT_STICKERS_FETCHED: function({products}, state){
			products.forEach(({id, stickers}) => {
				let item = state.search.items.find(o => o.id == `product(${id})`);
				if (!item) return;
				item.stickers = stickers;
			});
			return state;
		},

		PRICE_RANGE_FETCHED: function(action, state){
			if (action.query !== state.search.query) return state;

			var {exponent} = state.currency;
			var {min, max} = state.search.price;
			var {min:minRange, max: maxRange} = action;

			var x = 10 ** exponent;

			minRange = Math.round((minRange + Number.EPSILON) * x) / x;
			maxRange = Math.round((maxRange + Number.EPSILON) * x) / x;

			if (min != null && max != null){
				min = (min == state.priceRange.min)
					? min = minRange
					: min = Math.max(min, minRange);

				max = (max == state.priceRange.max)
					? max = maxRange
					: max = Math.max(max, minRange);

				state.search.price.min = min;
				state.search.price.max = max;
			}

			state.priceRange.min = minRange;
			state.priceRange.max = maxRange;

			return state;
		},

		MORE_SEARCH_RESULT_FETCHED: function(action, state){
			if (action.query !== state.search.query) return state;
			
			state.search.state = SearchResultState.LOADED;

			var newItems = action.results.hits.hits.map(function(o){
				return o._source;
			});

			state.search.items = state.search.items.concat(newItems);

			if (state.search.page === 0) state.search.page = 1;

			state.search.totalItems = action.results.hits.total.value;
			state.search.totalPages = Math.min(10000, Math.ceil(action.results.hits.total.value / RESULT_PAGE_SIZE));
			state.search.loadingMore = false;

			return state;
		},

		'FACETS_FETCHED': function(action, state){
			if (action.query !== state.search.query) return state;

			state.facets = action.results;
			
			if (Object.keys(action.attributes).length === 0){
				state.filteredAttributes = calcFilteredAttributes(state);
			}

			return state;
		},

		'SELECT_SUGGESTED_QUERY': function(action, state){
			state.activeInputQuery = action.query;
			state.suggestions.activeQuery = null;

			trackSearch(action.query);
			return getCachedPredictedResultOrFetch(store, state, state.activeInputQuery);
		},

		'ACTIVE_SUGGESTION_UP': function(action, state){
			state.suggestions.activeQuery =
				calcWrap(state.suggestions.activeQuery, -1, state.suggestions.queries.length);

			return state;
		},

		'ACTIVE_SUGGESTION_DOWN': function(action, state){
			state.suggestions.activeQuery =
				calcWrap(state.suggestions.activeQuery, 1, state.suggestions.queries.length);

			return state;
		},

		'SELECT_ACTIVE_SUGGESTION': function(action, state){
			var index = state.suggestions.activeQuery;
			
			if (index === null) return state;

			var query = state.suggestions.queries[index];

			state.activeInputQuery = query.value || query;
			state.suggestions.activeQuery = null;

			trackSearch(state.activeInputQuery);


			return  getCachedPredictedResultOrFetch(store, state, state.activeInputQuery);
		},

		'INPUT_FOCUS': function(action, state){
			state.inputs.activeElement = state.inputs.elements.indexOf(action.input);
			if (state.inputs.activeElement === -1) state.inputs.activeElement = null;
			state.activeInputQuery = action.input.value;
			return state;
		},

		INPUT_FOCUS_LOST: function(action, state){
			state.inputs.activeElement = null;
			state.activeInputQuery = '';
			return state;
		},

		'INPUT_VALUE_CHANGE': function(action, state){
			var value = action.value;

			state.activeInputQuery = value;

			if (!value){
				state.suggestions.queries = [];
				state.suggestions.activeQuery = null;
				state.suggestions.items = [];
				return state;
			}

			return  getCachedPredictedResultOrFetch(store, state, value);
		},

		'PREDICTED_RESULT_FETCHED': function(action, state){
			state.suggestions.history.push(action.results);

			if (state.activeInputQuery === action.query){
				state.suggestions.queries = createSuggestedQueries(action.query, action.results.suggest);
				state.suggestions.activeQuery = null;
				state.suggestions.items = action.results.hits.hits.map(function(o){
					return o._source;
				});
			}

			return state;
		},

		'PREDICTED_RESULT_SELECT': function(action, state){
			trackSearch(state.activeInputQuery);

			return state;
		},

		'SIMILAR_RESULT_FETCHED': function(action, state){
			if (state.search.query === action.query){
				state.search.similarQueries = createSuggestedQueries(action.query, action.results.suggest);
				state.search.similarItems = action.results.hits.hits.map(function(o){
					return o._source;
				});
			}

			return state;
		},

		'SELECT_PAGE': function(action, state){
			if (action.page == state.search.page) return state;
			if (action.page < 1) return state;
			if (action.page > state.search.totalPages) return state;

			state.search.page = action.page;
			state.search.state = SearchResultState.LOADING;

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'SEARCH_RESULT_FETCHED',
					query: query,
					results: res
				});
			});

			window.scrollTo(0, 0);

			return state;
		},

		'LOAD_MORE': function(action, state){
			if (state.search.loadingMore) return state;
			if (state.search.page + 1 > state.search.totalPages) return state;

			state.search.page++;
			state.search.loadingMore = true;
			state.search.state = SearchResultState.LOADING;

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'MORE_SEARCH_RESULT_FETCHED',
					query: query,
					results: res
				});
			});

			return state;
		},

		'CHANGE_CATEGORY_FILTER': function(action, state){
			state.search.categories = action.categories;
			state.search.items = [];
			state.search.page = 0;
			state.search.totalPages = 0;
			state.search.totalItems = 0;

			state.suggestions.queries = [];
			state.suggestions.activeQuery = null;
			state.suggestions.items = [];

			state.search.state = SearchResultState.LOADING;

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'SEARCH_RESULT_FETCHED',
					query: query,
					results: res
				});
			});

			return state;
		},

		'ADD_ATTRIBUTE_FILTER': function(action, state){
			var attrs = state.search.attributes;

			attrs[action.name] = attrs[action.name] || [];
			attrs[action.name].push(action.value);

			state.search.items = [];
			state.search.page = 0;
			state.search.totalPages = 0;
			state.search.totalItems = 0;

			state.search.state = SearchResultState.LOADING;

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'SEARCH_RESULT_FETCHED',
					query,
					results: res
				});
			});

			return state;
		},

		'REMOVE_ATTRIBUTE_FILTER': function(action, state){
			var attrs = state.search.attributes;

			attrs[action.name] = attrs[action.name].filter(s => s != action.value)
			if (attrs[action.name].length === 0) delete attrs[action.name];

			state.search.items = [];
			state.search.page = 0;
			state.search.totalPages = 0;
			state.search.totalItems = 0;

			state.search.state = SearchResultState.LOADING;

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'SEARCH_RESULT_FETCHED',
					query: query,
					results: res
				});
			});

			return state;
		},

		'UPDATE_PRICE_RANGE_MIN': function(action, state){
			state.search.price.min = action.value;

			if (state.search.price.max == null){
				state.search.price.max = state.priceRange.max;
			}

			if (action.final){
				state.search.items = [];
				state.search.page = 0;
				state.search.totalPages = 0;
				state.search.totalItems = 0;

				state.search.state = SearchResultState.LOADING;

				fetchResultsFromState(state, ({res, query}) => {
					store.dispatch({
						type: 'SEARCH_RESULT_FETCHED',
						query: query,
						results: res
					});
				});
			}

			return state;
		},

		'UPDATE_PRICE_RANGE_MAX': function(action, state){
			state.search.price.max = action.value;
			
			if (state.search.price.min == null){
				state.search.price.min = state.priceRange.min;
			}

			if (action.final){
				state.search.items = [];
				state.search.page = 0;
				state.search.totalPages = 0;
				state.search.totalItems = 0;

				state.search.state = SearchResultState.LOADING;

				fetchResultsFromState(state, ({res, query}) => {
					store.dispatch({
						type: 'SEARCH_RESULT_FETCHED',
						query: query,
						results: res
					});
				});
			}
			
			return state;
		},

		'CLEAR_ALL_RECENT_SEARCHES': function(action, state){
			recentSearches.removeAll();
			state.recentQueries.items = [];
			return state;
		},

		'CLEAR_RECENT_SEARCH': function(action, state){
			recentSearches.removeValue(action.query);
			state.recentQueries.items = recentSearches.getAll();
			return state;
		},

		'SELECT_RECENT_SEARCH': function(action, state){
			state.activeInputQuery = action.query;
			state.recentQueries.activeItem = null;

			trackSearch(action.query);

			return getCachedPredictedResultOrFetch(store, state, state.activeInputQuery);
		},

		'VIEW_ALL_RESULTS': function(action, state){
			if (state.inputs.activeElement === null) return state;

			state.inputs.elements[state.inputs.activeElement].form.submit();

			return state;
		},

		'TOGGLE_FILTER_VISIBLE': function(action, state){
			state.ui.filtersVisible = !state.ui.filtersVisible;
			return state;
		},

		'TOGGLE_VIEW_STYLE': function(action, state){
			state.ui.gridView = !state.ui.gridView;
			state.ui.listView = !state.ui.gridView;
			return state;
		},

		CLEAR_FILTER: function(action, state){
			state.search.attributes = {};
			state.search.price = {
				min: null, 
				max: null
			};

			state.search.state = SearchResultState.LOADING;

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'SEARCH_RESULT_FETCHED',
					query: query,
					results: res
				});
			});

			return state;
		},

		TOGGLE_ATTRIBUTE_COLLAPSE: function(action, state){
			const {name} = action;
			if (state.collapsedAttributes.includes(name)){
				state.collapsedAttributes = state.collapsedAttributes.filter(x => x !== name);
			}
			else {
				state.collapsedAttributes.push(name);
			}

			return state;
		},

		HISTORY_POP_STATE: function(action, state){
			state.search.state = SearchResultState.LOADING;

			state.search.query = action.state.searchQuery;
			state.search.categories = action.state.searchCategories;
			state.search.attributes = action.state.searchAttributes;
			state.search.price.min = action.state.searchMinPrice;
			state.search.price.max = action.state.searchMaxPrice;

			state.search.items = [];
			state.search.page = 0;
			state.search.totalPages = 0;
			state.search.totalItems = 0;

			state.suggestions.queries = [];
			state.suggestions.activeQuery = null;
			state.suggestions.items = [];

			fetchResultsFromState(state, ({res, query}) => {
				store.dispatch({
					type: 'SEARCH_RESULT_FETCHED',
					query: query,
					results: res,
					historyRestore: true
				});
			});

			return state;
		},

		'WINDOW_RESIZE': function(action, state){
			return state;
		}
	};

	return store = new Store(state, function(action, state){
		//console.log('%c%s', 'color:purple', action.type);
		var fn = fns[action.type];
		if (fn) return fn(action, state);
		return state;
	});
}